import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'stores/store';
import dayjs, { Dayjs } from 'dayjs';
import { User } from 'stores/auth/authSlice';
import { PatientStatus, RequestStatus, REQUEST_STATUSES } from 'stores/common';
import {
  generateCerfa,
  getPatient,
  getPatients,
  getPPCPatients,
  patchPatient,
} from './patientsClient';
import type {
  Insured,
  Patient,
  Supported,
  Treatment,
  Prescription,
} from './patientsTypes';

export interface PatientsState {
  current?: Patient;
  currentRequestStatus?: RequestStatus;
  updateCurrentRequestStatus?: RequestStatus;
  list: Patient[];
  listRequestStatus?: RequestStatus;
  ppcList: Patient[];
  ppcListRequestStatus?: RequestStatus;
  generatedCERFAContent?: string,
  generatedCERFARequestStatus?: RequestStatus;
}

const initialState: PatientsState = {
  list: [],
  ppcList: [],
};

export const getPatientAsync = createAsyncThunk(
  'patients/current',
  (patientID: string) => getPatient(patientID),
);

export const generateCerfaAsync = createAsyncThunk(
  'patients/currentGenerateCerfa',
  ({
    patient,
    doctor,
    insured,
    supported,
    treatment,
    prescription,
  }: {
    patient: Patient,
    insured: Insured,
    supported: Supported,
    treatment: Treatment,
    doctor: User,
    prescription: Prescription,
  }) => generateCerfa(
    patient,
    insured,
    supported,
    treatment,
    doctor,
    prescription,
  ),
);

export interface IGetPatientListAsync {
  name?: string;
  status?: PatientStatus | null;
  endDate?: Dayjs | null;
  page?: number;
  startDate?: Dayjs | null;
  doctorID?: string;
  pageSize?: number;
  sortOrder?: 'ascend' | null;
}

let getPatientListAsyncController: AbortController;

export const getPatientListAsync = createAsyncThunk(
  'patients/list',
  (params: IGetPatientListAsync) => {
    const {
      name,
      status = null,
      endDate,
      page = 1,
      startDate,
      doctorID = undefined,
      pageSize = 10,
      sortOrder = null,
    } = params;
    const searchParams = new URLSearchParams({
      page_size: pageSize.toString(),
      page: page.toString(),
    });

    if (name) {
      searchParams.append('name', name);
    }
    if (startDate) {
      searchParams.append('start_date', dayjs(startDate).format('YYYY-MM-DD'));
    }
    if (endDate) {
      searchParams.append('end_date', dayjs(endDate).format('YYYY-MM-DD'));
    }
    if (status) {
      searchParams.append('status', status);
    }
    if (doctorID) {
      searchParams.append('doctor_id', doctorID);
    }
    if (sortOrder === 'ascend') {
      searchParams.append('sort_order', 'ascending');
    }

    if (getPatientListAsyncController?.abort) {
      getPatientListAsyncController.abort();
    }

    getPatientListAsyncController = new AbortController();

    return getPatients(searchParams, getPatientListAsyncController.signal);
  },
);

let getPPCPatientListAsyncController: AbortController;

export const getPPCPatientListAsync = createAsyncThunk(
  'patients/PPCList',
  (params: IGetPatientListAsync) => {
    const {
      name,
      status = null,
      endDate,
      page = 1,
      startDate,
      doctorID = undefined,
      pageSize = 10,
      sortOrder = null,
    } = params;
    const searchParams = new URLSearchParams({
      page_size: pageSize.toString(),
      page: page.toString(),
    });

    if (name) {
      searchParams.append('name', name);
    }
    if (startDate) {
      searchParams.append('start_date', dayjs(startDate).format('YYYY-MM-DD'));
    }
    if (endDate) {
      searchParams.append('end_date', dayjs(endDate).format('YYYY-MM-DD'));
    }
    if (status) {
      searchParams.append('status', status);
    }
    if (doctorID) {
      searchParams.append('doctor_id', doctorID);
    }
    if (sortOrder === 'ascend') {
      searchParams.append('sort_order', 'ascending');
    }

    if (getPPCPatientListAsyncController?.abort) {
      getPPCPatientListAsyncController.abort();
    }

    getPPCPatientListAsyncController = new AbortController();

    return getPPCPatients(searchParams, getPPCPatientListAsyncController.signal);
  },
);

export interface IUpdatePatientAsync {
  patientID: string;
  observations?: string;
  status?: PatientStatus;
}
export const updatePatientAsync = createAsyncThunk(
  'patients/update',
  ({ patientID, observations, status }: IUpdatePatientAsync) => patchPatient(
    patientID,
    {
      observations,
      status,
    },
  ),
);

export const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    resetCurrentPatient: (state) => {
      delete state.current;
      delete state.currentRequestStatus;
      delete state.updateCurrentRequestStatus;
      delete state.generatedCERFAContent;
    },
    resetPatientLists: (state) => {
      state.list = [];
      delete state.listRequestStatus;
      state.ppcList = [];
      delete state.ppcListRequestStatus;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPatientAsync.pending, (state) => {
        state.currentRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getPatientAsync.fulfilled, (state, { payload }) => {
        state.currentRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.current = payload;
      })
      .addCase(getPatientAsync.rejected, (state) => {
        state.currentRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getPatientListAsync.pending, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getPatientListAsync.fulfilled, (state, { payload }) => {
        state.listRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.list = payload;
      })
      .addCase(getPatientListAsync.rejected, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getPPCPatientListAsync.pending, (state) => {
        state.ppcListRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getPPCPatientListAsync.fulfilled, (state, { payload }) => {
        state.ppcListRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.ppcList = payload;
      })
      .addCase(getPPCPatientListAsync.rejected, (state) => {
        state.ppcListRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(generateCerfaAsync.pending, (state) => {
        state.generatedCERFAContent = '';
        state.generatedCERFARequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(generateCerfaAsync.fulfilled, (state, { payload }) => {
        state.generatedCERFAContent = payload;
        state.generatedCERFARequestStatus = REQUEST_STATUSES.SUCCESS;
      })
      .addCase(generateCerfaAsync.rejected, (state) => {
        state.generatedCERFARequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(updatePatientAsync.pending, (state) => {
        state.updateCurrentRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(updatePatientAsync.fulfilled, (state, { meta }) => {
        state.updateCurrentRequestStatus = REQUEST_STATUSES.SUCCESS;
        if (state.current && meta.arg.observations) {
          state.current.rdv_3___observations_medecin = meta.arg.observations;
        }
        if (state.current && meta.arg.status) {
          state.current.rdv_3___statut2 = meta.arg.status;
        }
      })
      .addCase(updatePatientAsync.rejected, (state) => {
        state.updateCurrentRequestStatus = REQUEST_STATUSES.ERROR;
      });
  },
});

export const { resetCurrentPatient, resetPatientLists } = patientsSlice.actions;
export const getGeneratedCerfa = (state: RootState) => state.patients.generatedCERFAContent;
export const getGeneratedPDFRequestStatus = (state: RootState) => (
  state.patients.generatedCERFARequestStatus
);
export const getCurrentPatient = (state: RootState) => state.patients.current;
export const getCurrentPatientRequestStatus = (
  state: RootState,
) => state.patients.currentRequestStatus;
export const getUpdatePatientRequestStatus = (
  state: RootState,
) => state.patients.updateCurrentRequestStatus;
export const getPatientList = (state: RootState) => state.patients.list;
export const getPatientListRequestStatus = (
  state: RootState,
) => state.patients.listRequestStatus;
export const getPPCPatientList = (state: RootState) => state.patients.ppcList;
export const getPPCPatientListRequestStatus = (
  state: RootState,
) => state.patients.ppcListRequestStatus;

export default patientsSlice.reducer;
